package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.render.layer.UnsupportedCapabilityCapture
import top.wcpe.allincore.ui.runtime.render.layer.UnsupportedRenderCapability
import top.wcpe.allincore.ui.runtime.render.transform.AffineMatrix3
import top.wcpe.allincore.ui.runtime.render.transform.Matrix4x4Builder
import top.wcpe.allincore.ui.runtime.render.transform.Transform2D
import top.wcpe.allincore.ui.runtime.style.ColorMatrix
import kotlin.jvm.JvmField
import kotlin.jvm.JvmName
import kotlin.jvm.JvmStatic

/**
 * 单次渲染 pass 内的 runtime transform/alpha 状态栈。
 *
 * 并存维护 [Transform2D]（向后兼容）与 [AffineMatrix3]（旋转/倾斜/透视）。
 * 仅当元素含 rotation/skewX/skewY/perspective 时升级到 3×3 路径。
 */
@Suppress("TooManyFunctions", "MagicNumber", "ComplexCondition", "CyclomaticComplexMethod", "LongMethod")
internal class RenderStateStack {

    private val states = ArrayList<RenderState>()
    private var warnedUnsupportedTransform = false
    private var warnedUnsupportedAlpha = false
    private var warnedUnsupportedFullTransform = false
    private var warnedUnsupportedColorMatrix = false
    private var transform: Transform2D = Transform2D.IDENTITY
    private var matrix: AffineMatrix3 = AffineMatrix3.IDENTITY

    /**
     * 进入一个元素的渲染作用域。
     *
     * @param bridge      平台桥接
     * @param element     当前元素
     * @param absoluteBox 元素的绝对屏幕坐标包围盒，用于把旋转/倾斜中心补偿到元素中心；
     *                    传 null 时退化为「绕屏幕原点旋转」（仅供旧测试路径，不建议生产使用）
     */
    @Suppress("LongMethod", "CyclomaticComplexMethod")
    @JvmName("push")
    fun push(bridge: UiRenderBridge, element: UIElement<*>, absoluteBox: Box?): RenderScope {
        val unsupportedCapture = bridge as? UnsupportedCapabilityCapture
        unsupportedCapture?.beginUnsupportedContext()
        val hasBasicTransform = element.translateX() != 0f ||
            element.translateY() != 0f ||
            element.scaleX() != 1f ||
            element.scaleY() != 1f
        // 非默认 transformOrigin（origin != 0.5,0.5）并入 full-transform 触发条件：
        // basic 路径（pushTransform tx/ty/sx/sy）在平台层绕屏幕原点缩放、不感知 origin，
        // 故声明了非默认原点且含 basic 变换（translate/scale）的元素强制走 3×3 路径，
        // 使缩放也绕指定原点（纯 translate 被一并升级亦无害：平移与原点无关，结果等价）。
        val hasNonDefaultOrigin = element.originX() != 0.5f || element.originY() != 0.5f
        val hasFullTransform = element.rotation() != 0f ||
            element.rotateX() != 0f ||
            element.rotateY() != 0f ||
            element.skewX() != 0f ||
            element.skewY() != 0f ||
            element.perspective() != UIElement.DEFAULT_PERSPECTIVE ||
            (hasNonDefaultOrigin && hasBasicTransform)
        var pushedTransform = hasBasicTransform || hasFullTransform
        var pushedAlpha = element.opacity() != 1f
        // 色彩矩阵 filter（E1）：与 opacity 同构，元素声明任一 filter 时合成 4×5 矩阵
        // 包住整棵子树（CSS filter 级联作用于元素及所有子孙）。
        var pushedColorMatrix = element.style().visual().hasColorFilter()
        var usedFullTransform = false

        if (pushedTransform) {
            if (hasFullTransform) {
                usedFullTransform = pushFullTransform(bridge, element, absoluteBox)
                if (!usedFullTransform) {
                    pushedTransform = pushBasicTransform(bridge, element, hasBasicTransform)
                }
            } else {
                pushedTransform = pushBasicTransform(bridge, element, true)
            }
        }

        if (pushedAlpha && bridge.supportsRuntimeAlpha()) {
            bridge.pushAlpha(element.opacity())
        } else if (pushedAlpha) {
            warnUnsupportedAlpha()
            pushedAlpha = false
        }

        if (pushedColorMatrix && bridge.supportsColorMatrix()) {
            bridge.pushColorMatrix(ColorMatrix.fromStyle(element.style().visual()))
        } else if (pushedColorMatrix) {
            (bridge as? UnsupportedCapabilityCapture)?.captureUnsupportedCapability(
                UnsupportedRenderCapability.COLOR_MATRIX,
            )
            warnUnsupportedColorMatrix()
            pushedColorMatrix = false
        }

        if (pushedTransform || usedFullTransform || pushedAlpha || pushedColorMatrix) {
            val used3D = usedFullTransform &&
                (
                    element.rotateX() != 0f ||
                        element.rotateY() != 0f ||
                        element.perspective() != UIElement.DEFAULT_PERSPECTIVE
                    )
            val state = RenderState(
                pushedTransform && !usedFullTransform,
                usedFullTransform,
                used3D,
                element.translateX(),
                element.translateY(),
                element.scaleX(),
                element.scaleY(),
                pushedAlpha,
                element.opacity(),
                pushedColorMatrix,
                transform,
                matrix,
            )
            states.add(state)
            if (state.hasTransform || state.hasFullTransform) {
                transform = transform.push(
                    state.translateX,
                    state.translateY,
                    state.scaleX,
                    state.scaleY,
                )
                matrix = matrix.multiply(buildElementMatrix(element))
            }
            return RenderScope(this, bridge, state, unsupportedCapture)
        }
        return RenderScope(this, bridge, null, unsupportedCapture)
    }

    /** 兼容旧调用：不带 absoluteBox 时按屏幕原点旋转（保持原有行为）。 */
    @JvmName("push")
    fun push(bridge: UiRenderBridge, element: UIElement<*>): RenderScope = push(bridge, element, null)

    /** 当前 3×3 仿射矩阵（供外部查询）。 */
    @JvmName("currentMatrix")
    fun currentMatrix(): AffineMatrix3 = matrix

    @JvmName("snapshot")
    fun snapshot(): Snapshot = Snapshot(ArrayList(states))

    @JvmName("transformedBox")
    fun transformedBox(x: Float, y: Float, width: Float, height: Float): FloatArray {
        val full = transform.transformedBox(x, y, width, height)
        return floatArrayOf(full[0], full[1], full[2], full[3])
    }

    private fun pop(bridge: UiRenderBridge, state: RenderState?) {
        if (state == null) {
            return
        }
        states.removeAt(states.size - 1)
        if (state.hasTransform || state.hasFullTransform) {
            transform = state.previousTransform
            matrix = state.previousMatrix
        }
        // 按 push 逆序弹出：colorMatrix（最后入）先弹，再 alpha，最后 transform。
        if (state.hasColorMatrix) {
            bridge.popColorMatrix()
        }
        if (state.hasAlpha) {
            bridge.popAlpha()
        }
        if (state.hasFullTransform) {
            if (state.has3D) {
                bridge.popTransform4x4()
            } else {
                bridge.popTransform3x3()
            }
        } else if (state.hasTransform) {
            bridge.popTransform()
        }
    }

    /**
     * 推入 3D / 完整变换。
     *
     * 含 rotateX/rotateY 或非默认 perspective 时走 4×4 路径（含透视除法），
     * 仅 2D 旋转/倾斜时仍走 3×3 路径。
     *
     * 把旋转/倾斜中心补偿到元素中心（absoluteBox != null 时）：先平移到中心，
     * 再旋转/倾斜/缩放，最后平移回原位。这样直观符合「以自身中心旋转」的预期。
     */
    private fun pushFullTransform(bridge: UiRenderBridge, element: UIElement<*>, absoluteBox: Box?): Boolean {
        val has3D = element.rotateX() != 0f ||
            element.rotateY() != 0f ||
            element.perspective() != UIElement.DEFAULT_PERSPECTIVE
        if (!bridge.supportsFullTransform()) {
            (bridge as? UnsupportedCapabilityCapture)?.captureUnsupportedCapability(
                if (has3D) {
                    UnsupportedRenderCapability.FULL_TRANSFORM_4X4
                } else {
                    UnsupportedRenderCapability.FULL_TRANSFORM_3X3
                },
            )
            warnUnsupportedFullTransform()
            return false
        }
        if (has3D) {
            // 变换原点折算为屏幕绝对坐标：cx = box.x + w*originX、cy = box.y + h*originY。
            // 默认 originX/originY=0.5 时逐位等于历史的 w/2、h/2；absoluteBox==null 仍退化为 0f。
            val cx = if (absoluteBox != null) absoluteBox.x() + absoluteBox.width() * element.originX() else 0f
            val cy = if (absoluteBox != null) absoluteBox.y() + absoluteBox.height() * element.originY() else 0f
            val mat4 = Matrix4x4Builder.fromElement(element, cx, cy)
            bridge.pushTransform4x4(mat4)
            return true
        }
        var local = buildElementMatrix(element)
        if (absoluteBox != null) {
            // 同 4×4 路径：旋转/倾斜/缩放中心按归一化原点折算到屏幕绝对坐标
            val cx = absoluteBox.x() + absoluteBox.width() * element.originX()
            val cy = absoluteBox.y() + absoluteBox.height() * element.originY()
            local = AffineMatrix3.translate(cx, cy)
                .multiply(local)
                .multiply(AffineMatrix3.translate(-cx, -cy))
        }
        bridge.pushTransform3x3(local.toArray())
        return true
    }

    /** 推入基本平移+缩放变换。返回是否成功推入。 */
    private fun pushBasicTransform(bridge: UiRenderBridge, element: UIElement<*>, hasBasicTransform: Boolean): Boolean {
        if (!hasBasicTransform) {
            return false
        }
        if (bridge.supportsRuntimeTransform()) {
            bridge.pushTransform(
                element.translateX(),
                element.translateY(),
                element.scaleX(),
                element.scaleY(),
            )
            return true
        }
        warnUnsupportedTransform()
        return false
    }

    private fun warnUnsupportedTransform() {
        if (!warnedUnsupportedTransform) {
            LOGGER.warn("UiRenderBridge 不支持 runtime transform，本帧 transform 状态将被跳过")
            warnedUnsupportedTransform = true
        }
    }

    private fun warnUnsupportedAlpha() {
        if (!warnedUnsupportedAlpha) {
            LOGGER.warn("UiRenderBridge 不支持 runtime alpha，本帧 alpha 状态将被跳过")
            warnedUnsupportedAlpha = true
        }
    }

    private fun warnUnsupportedFullTransform() {
        if (!warnedUnsupportedFullTransform) {
            LOGGER.warn("UiRenderBridge 不支持 3×3 仿射变换，旋转/倾斜/透视将被忽略并降级到平移+缩放")
            warnedUnsupportedFullTransform = true
        }
    }

    private fun warnUnsupportedColorMatrix() {
        if (!warnedUnsupportedColorMatrix) {
            LOGGER.warn("UiRenderBridge 不支持 runtime 色彩矩阵，本帧 filter 状态将被跳过")
            warnedUnsupportedColorMatrix = true
        }
    }

    internal class RenderScope internal constructor(
        private val owner: RenderStateStack,
        private val bridge: UiRenderBridge,
        private val state: RenderState?,
        private val unsupportedCapture: UnsupportedCapabilityCapture?,
    ) : AutoCloseable {

        override fun close() {
            try {
                owner.pop(bridge, state)
            } finally {
                unsupportedCapture?.endUnsupportedContext()
            }
        }
    }

    internal class Snapshot internal constructor(private val states: List<RenderState>) {

        @Suppress("MagicNumber")
        fun push(bridge: UiRenderBridge) {
            for (state in states) {
                if (state.hasFullTransform) {
                    // Snapshot 重放时复原完整变换
                    bridge.pushTransform3x3(
                        floatArrayOf(
                            state.scaleX, 0f, state.translateX,
                            0f, state.scaleY, state.translateY,
                            0f, 0f, 1f,
                        ),
                    )
                } else if (state.hasTransform) {
                    bridge.pushTransform(
                        state.translateX,
                        state.translateY,
                        state.scaleX,
                        state.scaleY,
                    )
                }
                if (state.hasAlpha) {
                    bridge.pushAlpha(state.alpha)
                }
            }
        }

        fun pop(bridge: UiRenderBridge) {
            for (i in states.indices.reversed()) {
                val state = states[i]
                if (state.hasAlpha) {
                    bridge.popAlpha()
                }
                if (state.hasFullTransform) {
                    if (state.has3D) {
                        bridge.popTransform4x4()
                    } else {
                        bridge.popTransform3x3()
                    }
                } else if (state.hasTransform) {
                    bridge.popTransform()
                }
            }
        }
    }

    internal class RenderState(
        @JvmField val hasTransform: Boolean,
        @JvmField val hasFullTransform: Boolean,
        @JvmField val has3D: Boolean,
        @JvmField val translateX: Float,
        @JvmField val translateY: Float,
        @JvmField val scaleX: Float,
        @JvmField val scaleY: Float,
        @JvmField val hasAlpha: Boolean,
        @JvmField val alpha: Float,
        @JvmField val hasColorMatrix: Boolean,
        @JvmField val previousTransform: Transform2D,
        @JvmField val previousMatrix: AffineMatrix3,
    )

    internal companion object {
        private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.render.RenderStateStack")

        /** 根据元素属性构建局部 3×3 矩阵（委托到 [AffineMatrix3.fromElement]）。 */
        @JvmStatic
        @JvmName("buildElementMatrix")
        fun buildElementMatrix(el: UIElement<*>): AffineMatrix3 = AffineMatrix3.fromElement(el)
    }
}
